import { motion } from "framer-motion";
import { Cursor, useTypewriter } from "react-simple-typewriter";
import { shakilAhmmed } from "../assets/index";
import Socials from "./layouts/Socials";

const statChips = [
  { top: "4+", bottom: "Years Experience" },
  { top: "20+", bottom: "Systems Delivered" },
  { top: "60%", bottom: "Business Impact" },
];



const Banner = () => {
  const [text] = useTypewriter({
    words: [
      // "Architecting platforms that scale predictably with business growth.",
      // "Aligning engineering execution with executive business strategy.",
      // "Driving systemic reliability and autonomous operations.",
      "Software Engineer",
      "Web Developer",
      "Problem Solver"
    ],
    loop: true,
    typeSpeed: 40,
    deleteSpeed: 15,
    delaySpeed: 2000,
  });

  return (
    <section
      id="home"
      aria-label="Hero section"
      className="w-full py-20 lgl:py-32 flex flex-col gap-10 xl:gap-0 lgl:flex-row items-center border-b-[1px] border-b-gray-800"
    >
      {/* ── Left column ─────────────────────────────────────── */}
      <div className="flex flex-col w-full h-full gap-10 xl:w-1/2">
        <div className="flex flex-col gap-4 font-titleFont">

          {/* Availability */}
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.6 }}
          >
            <span className="status-badge">
              <span className="status-dot" />
              Available for Remote Opportunities
            </span>
          </motion.div>

          {/* Name */}
          <motion.h1
            initial={{ x: -80, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{
              delay: 0.3,
              x: { type: "spring", stiffness: 30 },
              opacity: { duration: 0.6 },
              duration: 1,
            }}
            className="text-3xl font-bold text-white md:text-5xl lgl:text-6xl leading-tight"
          >
            Shakil Ahmmed
          </motion.h1>

          {/* Title hierarchy */}
          <motion.div
            initial={{ x: -80, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{
              delay: 0.4,
              x: { type: "spring", stiffness: 30 },
              opacity: { duration: 0.5 },
              duration: 1,
            }}
          >
            {/* <p className="text-xl font-bold text-designColor md:text-2xl tracking-wide">
              Software Engineer - (Full Stack)
            </p> */}
            {/* <p className="text-sm font-semibold text-designColor mt-1 tracking-wide">
              Building scalable software through thoughtful engineering
            </p> */}
            <p className="text-3xl font-bold text-designColor md:text-3xl lgl:text-4xl tracking-wide">
              <span>{text}</span>
              <Cursor cursorBlinking={true} cursorStyle="_" cursorColor="#0A5CE5" />
            </p>
          </motion.div>

          {/* Value proposition */}
          <motion.p
            initial={{ x: -80, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{
              delay: 0.5,
              x: { type: "spring", stiffness: 30 },
              opacity: { duration: 0.4 },
              duration: 1,
            }}
            className="text-sm leading-7 text-gray-400 max-w-lg"
          >
            I architect platforms that scale businesses. I bridge the gap between executive
            strategy and engineering execution—ensuring we build the right systems, not just build
            systems right. My focus is on systemic reliability, enforcing data isolation across
            multi-tenant environments, and building internal capabilities that allow non-technical
            teams to operate independently of the engineering queue.
          </motion.p>
        </div>

        {/* Stat chips */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="flex flex-wrap gap-3"
        >
          {statChips.map((c, i) => (
            <motion.div
              key={i}
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.7 + i * 0.1, duration: 0.5 }}
              whileHover={{ y: -3 }}
              className="group flex flex-1 flex-col items-center justify-center px-4 py-3 bg-[#0F172A] border border-gray-800 border-designColor/50 shadow-designColor/10 text-center rounded-xl shadow-lg"
            >
              <span className="text-xl font-extrabold text-designColor leading-none">
                {c.top}
              </span>
              <span className="text-[11px] text-gray-400 font-medium uppercase tracking-[0.15em] mt-1.5">
                {c.bottom}
              </span>
            </motion.div>
          ))}
        </motion.div>


        {/* Socials + CTAs */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="flex flex-col gap-6"
        >
          <div>
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-widest text-gray-400 font-titleFont">
              Connect
            </h2>
            <div className="flex gap-3">
              <Socials />
            </div>
          </div>
        </motion.div>
      </div>

      {/* ── Right column — profile image ─────────────────── */}
      <div className="banner-image-container float-subtle w-[90%] h-full max-w-[380px] xl:w-1/2 mx-auto flex justify-center items-center relative rounded-xl">
        <div className="relative z-20 w-full h-full overflow-hidden rounded-2xl">
          <motion.img
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, type: "spring", stiffness: 60, opacity: { duration: 0.5 }, duration: 0.5 }}
            className="relative object-cover w-full h-full rounded-2xl grayscale hover:grayscale-0 transition-all ease-in-out duration-500"
            src={shakilAhmmed}
            alt="Shakil Ahmmed — Software Systems Engineer"
            fetchpriority="high"
          />
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, type: "spring", stiffness: 60, opacity: { duration: 0.5 }, duration: 0.8 }}
          className="absolute inset-0 after:content-[''] before:content-[''] before:absolute after:absolute transform after:translate-x-4 after:translate-y-4 before:-translate-x-4 before:-translate-y-3 after:border-2 before:border-2 after:border-designColor before:border-designColor after:rounded-2xl before:rounded-2xl after:w-full after:h-full before:w-full before:h-full bg-none after:opacity-40 before:opacity-40"
        />
      </div>
    </section>
  );
};

export default Banner;
